import { useState, useEffect } from 'react';
import { Menu, X, Globe } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { logoAuxis } from '../../assets/images';
import { useTranslation } from 'react-i18next';

const Navbar = () => {
  const { t, i18n } = useTranslation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeLink, setActiveLink] = useState('home');
  const language = i18n.language && i18n.language.startsWith('en') ? 'en' : 'fr';

  // Memoize currentLinks to avoid warnings and unnecessary re-renders
  const currentLinks = [
    { id: 'home', label: t('nav.home') },
    { id: 'services', label: t('nav.services') },
    { id: 'projects', label: t('nav.projects') },
    { id: 'expertises', label: t('nav.expertises') },
    { id: 'references', label: t('nav.references') },
    { id: 'contact', label: t('nav.contact') }
  ];

  useEffect(() => {
    let ticking = false;

    const updateNavbarLogic = () => {
      setIsScrolled(window.scrollY > 20);
        
      // Check if we are near the bottom of the page
      if (Math.ceil(window.innerHeight + window.scrollY) >= document.documentElement.scrollHeight - 150) {
        setActiveLink('contact');
        ticking = false;
        return;
      }

      const sections = currentLinks.map(link => document.getElementById(link.id));
      // Trigger active state when section reaches upper 30% of the screen
      const scrollPosition = window.scrollY + window.innerHeight * 0.3;
      
      for (let i = sections.length - 1; i >= 0; i--) {
        if (sections[i] && sections[i].offsetTop <= scrollPosition) {
          setActiveLink(currentLinks[i].id);
          break;
        }
      }
      ticking = false;
    };

    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(updateNavbarLogic);
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    updateNavbarLogic(); // Trigger once on mount

    return () => window.removeEventListener('scroll', handleScroll);
  }, [i18n.language]);

  const toggleLanguage = () => {
    i18n.changeLanguage(language === 'en' ? 'fr' : 'en');
  };

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) {
      const offset = 80;
      const top = el.getBoundingClientRect().top + window.pageYOffset - offset;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  };

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ type: 'spring', stiffness: 80, damping: 20, delay: 0.2 }}
      className={`fixed w-full z-50 transition-all duration-700 ease-out-expo ${
        isScrolled
          ? 'py-2'
          : 'py-4'
      }`}
    >
      <div className="container-wide">
        <div className={`flex items-center justify-between rounded-2xl transition-all duration-700 ease-out-expo ${
          isScrolled
            ? 'bg-white/80 dark:bg-auxis-charcoal/80 backdrop-blur-2xl border border-gray-100 dark:border-white/10 px-6 py-2 shadow-sm'
            : 'px-0 py-0'
        }`}>
          {/* Logo */}
          <motion.a
            href="#home"
            onClick={(e) => { e.preventDefault(); scrollToSection('home'); }}
            className="flex-shrink-0 relative z-10 transition-colors duration-300"
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
          >
            <img src={logoAuxis} alt="AUXIS" className="h-9 md:h-10 opacity-100" />
          </motion.a>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-1">
            {currentLinks.map(link => (
              <motion.a
                key={link.id}
                href={`#${link.id}`}
                onClick={(e) => { e.preventDefault(); scrollToSection(link.id); setActiveLink(link.id); }}
                className={`relative px-4 py-2 text-sm font-medium transition-colors duration-300 ${
                  activeLink === link.id
                    ? 'text-auxis-gold'
                    : 'text-auxis-charcoal dark:text-gray-300 hover:text-auxis-gold dark:hover:text-auxis-gold'
                }`}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                {link.label}
                {activeLink === link.id && (
                  <motion.div
                    className="absolute bottom-0 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-auxis-gold"
                    layoutId="nav-dot"
                    transition={{ type: "spring", stiffness: 400, damping: 30 }}
                  />
                )}
              </motion.a>
            ))}

            <div className="w-px h-4 bg-gray-200 dark:bg-gray-700 mx-4" />

            {/* Language Toggle */}
            <motion.button
              onClick={toggleLanguage}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-auxis-charcoal dark:text-white hover:text-auxis-gold dark:hover:text-auxis-gold transition-all duration-300"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Globe size={14} />
              <span className="uppercase text-xs tracking-wider">{language}</span>
            </motion.button>

            {/* CTA Button */}
            <motion.a
              href="#contact"
              onClick={(e) => { e.preventDefault(); scrollToSection('contact'); }}
              className="ml-3 px-5 py-2 text-sm font-semibold rounded-full border border-auxis-gold text-auxis-gold hover:bg-auxis-gold hover:text-white transition-all duration-500"
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
            >
              {t('nav.quote')}
            </motion.a>
          </div>

          {/* Mobile Controls */}
          <div className="lg:hidden flex items-center gap-3">
            <motion.button
              onClick={toggleLanguage}
              className="p-2 text-auxis-charcoal dark:text-white hover:text-auxis-gold transition-colors"
              whileTap={{ scale: 0.9 }}
            >
              <Globe size={17} />
            </motion.button>
            <motion.button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="p-2 text-auxis-charcoal dark:text-white transition-colors"
              whileTap={{ scale: 0.9 }}
            >
              <AnimatePresence mode="wait" initial={false}>
                <motion.div
                  key={isMenuOpen ? 'close' : 'menu'}
                  initial={{ opacity: 0, rotate: -90 }}
                  animate={{ opacity: 1, rotate: 0 }}
                  exit={{ opacity: 0, rotate: 90 }}
                  transition={{ duration: 0.15 }}
                >
                  {isMenuOpen ? <X size={22} /> : <Menu size={22} />}
                </motion.div>
              </AnimatePresence>
            </motion.button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            className="lg:hidden absolute top-full left-4 right-4 mt-2 bg-white/95 dark:bg-auxis-charcoal/95 backdrop-blur-2xl rounded-2xl border border-gray-100 dark:border-white/10 shadow-xl overflow-hidden"
            initial={{ opacity: 0, y: -10, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.98 }}
            transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className="p-5 space-y-1">
              {currentLinks.map((link, index) => (
                <motion.a
                  key={link.id}
                  href={`#${link.id}`}
                  onClick={(e) => { e.preventDefault(); scrollToSection(link.id); setIsMenuOpen(false); }}
                  className={`block px-4 py-3 text-base font-medium rounded-xl transition-all duration-200 ${
                    activeLink === link.id
                      ? 'text-auxis-gold bg-auxis-gold/10'
                      : 'text-auxis-charcoal/80 dark:text-gray-300 hover:text-auxis-charcoal dark:hover:text-white hover:bg-gray-50 dark:hover:bg-white/5'
                  }`}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  {link.label}
                </motion.a>
              ))}
              <div className="pt-4">
                <a
                  href="#contact"
                  onClick={(e) => { e.preventDefault(); scrollToSection('contact'); setIsMenuOpen(false); }}
                  className="block w-full py-3 text-center bg-auxis-gold text-dark font-semibold rounded-xl"
                >
                  {t('nav.quote')}
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};

export default Navbar;